import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { CalendarClock, Check, Lock } from "lucide-react"
import type { User } from "@pishgam/contracts"

import { api } from "../api"
import { EmptyState, fiscalYearLabel, listKeys, LoadingCard, Page } from "../shared"
import { fa, type TasksResponse } from "../process"
import { fiscalChip, fiscalYears, YearPicker } from "@/components/period-picker"
import { PlanPanel } from "@/components/plan-panel"
import { useUnsavedGuard } from "@/components/spreadsheet"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

type Plan = { round: number; isActive: boolean; label: string }

/**
 * Business heads: their unit's forecast next to the realized figures finance enters (always
 * read-only here). Any fiscal year and round can be viewed; only the round of the open window
 * (calendar or admin-opened) is editable.
 */
export function UnitForecastPage({ user }: { user: User }) {
  const [params, setParams] = useSearchParams()
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const window = tasks.data?.forecast
  const today = tasks.data?.cycle.today
  const currentFy = today ? (today[1] >= 7 ? today[0] : today[0] - 1) : 0
  const fy = Number(params.get("fy") ?? (window?.open ? window.fiscalYear : currentFy))
  const plans = useQuery({ queryKey: ["finance-plans", fy], queryFn: () => api<Plan[]>(`/finance/plans/${fy}`), enabled: fy > 0 })
  const defaultRound = window?.open && window.fiscalYear === fy && plans.data?.some((plan) => plan.round === window.round)
    ? window.round
    : plans.data?.find((plan) => plan.isActive)?.round ?? plans.data?.at(-1)?.round
  const round = Number(params.get("round") ?? defaultRound ?? 0)
  const editing = Boolean(window?.open && window.fiscalYear === fy && window.round === round)
  const [dirty, setDirty] = useState(0)
  const guard = useUnsavedGuard(dirty)
  const select = (changes: Record<string, string | null>) => guard(() => setParams((current) => {
    const next = new URLSearchParams(current)
    for (const [key, value] of Object.entries(changes)) { if (value === null) next.delete(key); else next.set(key, value) }
    return next
  }, { replace: true }))

  return (
    <Page title="پیش‌بینی کسب‌وکار من" description="پیش‌بینی درآمد، هزینه و شاخص‌های کلیدی کسب‌وکاری که مدیر آن هستید، کنار ارقام محقق‌شده‌ای که مدیر مالی ثبت می‌کند. ارقام پولی به میلیون ریال هستند.">
      {!window || !currentFy ? <LoadingCard /> : (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <YearPicker years={fiscalYears(currentFy)} value={fy} onChange={(next) => select({ fy: String(next), round: null })} label={fiscalChip} ariaLabel="سال مالی" />
            {!!plans.data?.length && (
              <div className="flex flex-wrap items-center gap-1 rounded-lg border bg-muted p-1" role="radiogroup" aria-label="نسخه پیش‌بینی"
                onKeyDown={(event) => listKeys(event, "radio", (index) => { const item = plans.data?.[index]; if (item) select({ round: String(item.round) }) }, false)}>
                <span className="px-2 text-xs text-muted-foreground">نسخه:</span>
                {plans.data.map((item) => (
                  <button key={item.round} type="button" role="radio" aria-checked={item.round === round} tabIndex={item.round === round ? 0 : -1} onClick={() => select({ round: String(item.round) })}
                    className={cn("rounded-md px-3 py-1 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", item.round === round ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>
                    {item.label}{item.isActive && <span className="ms-1 inline-flex items-center gap-0.5 text-primary"><Check className="size-3" aria-hidden />مبنا</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div role="status" className={cn("flex flex-wrap items-start gap-3 rounded-xl border p-4", editing ? "border-primary/40 bg-primary/5" : "bg-muted/40")}>
            {editing ? <CalendarClock className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden /> : <Lock className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden />}
            <div className="min-w-0 flex-1 text-sm">
              {editing ? (
                <>
                  <p className="font-medium">پنجره بازنگری {fiscalYearLabel(window.fiscalYear)} تا {fa(window.endLabel)} باز است{window.manual ? " (باز شده توسط مدیر سامانه)" : ""}.</p>
                  <p className="text-muted-foreground">ردیف‌های «پیش‌بینی» قابل ویرایش‌اند؛ ردیف‌های «محقق» را مدیر مالی ثبت می‌کند. می‌توانید ردیف یا شاخص جدید هم تعریف کنید.</p>
                </>
              ) : window.open ? (
                <>
                  <p className="font-medium">این نسخه فقط قابل مشاهده است.</p>
                  <p className="text-muted-foreground">پنجره بازنگری {fiscalYearLabel(window.fiscalYear)} تا {fa(window.endLabel)} باز است؛ برای ویرایش، همان سال و نسخه را انتخاب کنید.</p>
                </>
              ) : (
                <>
                  <p className="font-medium">پنجره بازنگری اکنون بسته است.</p>
                  <p className="text-muted-foreground">پنجره بعدی: {fa(`1 تا ${window.endLabel}`)}. تا آن زمان ارقام فقط قابل مشاهده‌اند.</p>
                </>
              )}
            </div>
            {window.open && !editing && <Button size="sm" onClick={() => select({ fy: String(window.fiscalYear), round: String(window.round) })}>رفتن به نسخه قابل ویرایش</Button>}
          </div>

          {plans.isLoading ? <LoadingCard /> : !round || !plans.data?.some((plan) => plan.round === round) ? (
            <EmptyState text={editing ? "نسخه بازنگری در حال آماده‌سازی است؛ چند دقیقه دیگر دوباره سر بزنید." : `هنوز برنامه‌ای برای ${fiscalYearLabel(fy)} ثبت نشده است.`} />
          ) : (
            <PlanPanel key={`${fy}-${round}`} fy={fy} round={round} unitIds={user.capabilities.forecastUnits} editable={editing ? "forecast" : "none"} kinds={["revenue", "cost", "kpi", "assumption"]} canAddLines={editing} onDirty={setDirty} />
          )}
        </>
      )}
    </Page>
  )
}
